import type { CopilotSession } from '@customerio/hydra-copilot';
import type { CopilotAgentModel } from '@customerio/hydra-ui-api';
import { useCopilotModel } from '@customerio/hydra-copilot/react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuTriggerButton,
  Strong,
  Text,
  Tooltip,
} from '@customerio/pluma-components/react';
import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';
import { hydraUiApi, shouldRetry } from '@/services/api';
import { useAuth } from '@/services/auth';
import { AccountFeatureFlag, useFeatureFlag } from '@/services/auth/feature-flags';
import * as styles from '../../index.css';

const MODELS_STALE_TIME = 60 * 60 * 1000;

function useAgentModels(isEnabled: boolean) {
  const { account } = useAuth();
  const accountId = account?.id;

  const { data = [], isSuccess } = useQuery({
    queryKey: ['agent-models', accountId],
    enabled: isEnabled && accountId != null,
    staleTime: MODELS_STALE_TIME,
    // Without this the default 5m collection undoes the staleTime whenever the panel unmounts.
    gcTime: MODELS_STALE_TIME,
    // Don't retry a 404 (the account isn't enabled); keep the 5xx/network retries.
    retry: shouldRetry,
    queryFn: async () => {
      const response = await hydraUiApi.copilot.getAgentModels({
        params: { path: { account_id: String(accountId) } },
      });

      return response.models ?? [];
    },
  });

  // The server is the authority on whether this account may pick; a 200 means yes.
  return { models: data, canPick: isSuccess };
}

type ModelPickerProps = {
  session: Pick<CopilotSession, '__model' | '__modelLocked' | 'setModel'>;
  // Shown in place of the catalog's own default when the conversation hasn't chosen — falls
  // back to the catalog's default itself if the catalog doesn't have this id (e.g. retired).
  defaultModelId?: string;
  // Lets a specific surface (e.g. outcomes) unlock the picker without widening it everywhere
  // ModelPicker is used.
  extraFlag?: AccountFeatureFlag;
};

/**
 * Lets the account choose which model a conversation runs on. Creating the conversation settles
 * the choice, so from then on this is a plain label rather than a menu.
 */
export function ModelPicker({ session, defaultModelId, extraFlag }: ModelPickerProps) {
  const hasOpusModelFlag = useFeatureFlag(AccountFeatureFlag.AgentOpusModel);
  const hasExtraFlag = useFeatureFlag(extraFlag ?? AccountFeatureFlag.AgentOpusModel);
  const flagEnabled = hasOpusModelFlag || (extraFlag != null && hasExtraFlag);
  const { model, isLocked } = useCopilotModel(session);
  const { models, canPick } = useAgentModels(flagEnabled);

  // The submenu is the whole catalog, featured included, so no model is only in one list.
  const { featured, byProvider, selected } = useMemo(() => {
    const groups = new Map<string, CopilotAgentModel[]>();
    for (const candidate of models) {
      const group = groups.get(candidate.provider);
      if (group != null) {
        group.push(candidate);
      } else {
        groups.set(candidate.provider, [candidate]);
      }
    }

    const chosen = models.find((candidate) => candidate.id === model);
    const catalogDefault = models.find((candidate) => candidate.default);
    const preferredDefault = models.find((candidate) => candidate.id === defaultModelId);

    return {
      featured: models.filter((candidate) => candidate.featured),
      byProvider: [...groups.entries()],
      // Only an exact match, or a default for a conversation that never chose —
      // falling back to a default for an unknown *chosen* id would name the wrong model.
      selected: chosen ?? (model === '' ? (preferredDefault ?? catalogDefault) : undefined),
    };
  }, [models, model, defaultModelId]);

  if (!canPick) {
    return null;
  }

  if (isLocked) {
    // canPick already gated a refused catalog, so the id fallback here only names a retired model.
    const settled = selected?.name ?? model.split('/').pop();
    if (settled == null || settled === '') {
      return null;
    }

    return (
      <Tooltip
        content={`This conversation runs on ${settled}. Start a new one to use a different model.`}
        placement="top"
        isCustom={true}
      >
        {(context) => (
          <Text
            size="xs"
            color="subtle"
            className={styles.modelLockedLabel}
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            {settled}
          </Text>
        )}
      </Tooltip>
    );
  }

  if (selected == null) {
    return null;
  }

  const modelItem = (candidate: CopilotAgentModel) => (
    <DropdownMenuItem
      key={candidate.id}
      trailingIcon={candidate.id === selected.id ? 'check' : undefined}
      onClick={() => session.setModel(candidate.id)}
    >
      {candidate.name}
    </DropdownMenuItem>
  );

  return (
    <DropdownMenu>
      <Tooltip
        content="Pick the model for this chat. It can't be changed once you send a message."
        placement="top"
        isCustom={true}
        floatingPluginOptions={{ focus: { enabled: false } }}
      >
        {(context) => (
          <DropdownMenuTriggerButton
            variant="secondary"
            type="button"
            size="sm"
            className={styles.borderlessTrigger}
            ref={context.reference.setReference}
            {...context.reference.getReferenceProps()}
          >
            <Strong>{selected.name}</Strong>
          </DropdownMenuTriggerButton>
        )}
      </Tooltip>
      <DropdownMenuContent className={styles.modelMenu}>
        <DropdownMenuGroup label="Model">{featured.map(modelItem)}</DropdownMenuGroup>
        <DropdownMenuGroup>
          <DropdownMenuSub>
            <DropdownMenuSubTrigger>View all models</DropdownMenuSubTrigger>
            <DropdownMenuContent className={styles.modelMenu}>
              {byProvider.map(([provider, providerModels]) => (
                <DropdownMenuGroup key={provider} label={provider}>
                  {providerModels.map(modelItem)}
                </DropdownMenuGroup>
              ))}
            </DropdownMenuContent>
          </DropdownMenuSub>
        </DropdownMenuGroup>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
